import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { importProductsApi } from "../../api/importService";
import { getErrorMessage } from "../../utils/errorHandler";
import FullPageLoader from "../../common/FullPageLoader";

export default function ImportProducts() {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const sampleFileUrl =
    location.state?.sampleFileUrl || "/test.products.reframed.csv";

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];

    if (!selected) return;

    if (!selected.name.endsWith(".csv")) {
      setError("Please select a CSV file.");
      return;
    }

    setFile(selected);
    setError("");
    setSuccess("");
  };

  const handleDrop = (e) => {
    e.preventDefault();

    const selected = e.dataTransfer.files[0];

    if (!selected) return;

    if (!selected.name.endsWith(".csv")) {
      setError("Please upload a CSV file.");
      return;
    }

    setFile(selected);
    setError("");
    setSuccess("");
  };

  const removeFile = () => {
    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a CSV file.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      setSummary(null);
      setProgress(10);

      const res = await importProductsApi(file, (event) => {
        if (event?.total) {
          const percent = Math.round((event.loaded * 100) / event.total);

          setProgress(percent < 95 ? percent : 95);
        }
      });

      setProgress(100);

      setSuccess(res?.message || "Products imported successfully.");

      const data = res?.data || res || {};

      setSummary({
        total: data?.totalRows ?? 0,
        inserted: data?.importedCount ?? 0,
        updated: data?.updatedCount ?? 0,
        duplicate: data?.duplicateCount ?? 0,
        failed: data?.failedCount ?? 0,
        errors: Array.isArray(data?.errors) ? data.errors : [],
        errorFileUrl: data?.errorFileUrl || "",
      });

      removeFile();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4 np-import-products-page">
      {loading && <FullPageLoader text="Importing products..." />}
      <div
        className="card border-0 shadow-sm"
        style={{
          maxWidth: "1300px",
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <div className="card-body border-bottom">
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-start gap-3">
              <div className="fs-4">
                <i className="fa fa-cloud-upload" />
              </div>

              <div>
                <h4 className="mb-1 fw-semibold">Upload your file for import products</h4>

                <p className="text-muted mb-0">
                  Upload one .csv file at a time. Max file size: 2MB.
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-4">
              <button
                type="button"
                className="btn btn-link text-dark text-decoration-none fw-semibold p-0"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = sampleFileUrl;
                  link.download = "product-import-template.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <i className="fa fa-download me-2" />
                Download template
              </button>

              <button type="button" className="btn-close" onClick={() => navigate("/products")} />
            </div>
          </div>
        </div>

        <div className="card-body p-4">
          {/* Error */}
          {error && <div className="alert alert-danger mb-3">{error}</div>}

          {/* Upload Area */}
          <div
            className="text-center d-flex flex-column justify-content-center align-items-center"
            style={{
              minHeight: "370px",
              border: "2px dashed #6c757d",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div
              className="d-flex justify-content-center align-items-center mb-4"
              style={{
                width: "105px",
                height: "105px",
                border: "1px solid #6c757d",
                borderRadius: "20px",
              }}
            >
              <i
                className="fa fa-cloud-upload"
                style={{
                  fontSize: "42px",
                }}
              />
            </div>

            <h4 className="fw-normal mb-3">
              Drag & Drop or <span className="fw-bold text-decoration-underline">Choose file</span>{" "}
              to upload
            </h4>

            <p className="mb-1 fw-semibold text-muted">Maximum File Size: 2MB</p>

            <p className="mb-0 fw-semibold text-muted">Allowed format: .csv</p>

            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept=".csv"
              onChange={handleFileChange}
            />
          </div>

          {/* Success Message */}
          {success && (
            <div className="d-flex align-items-center gap-2 mt-4 text-success">
              <i className="fa fa-check-circle" />
              <span className="fw-semibold">File uploaded successfully</span>
            </div>
          )}

          {/* Selected File */}
          {file && (
            <div
              className="mt-4 p-3 d-flex justify-content-between align-items-center"
              style={{
                borderTop: "1px solid #dee2e6",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{
                    width: "55px",
                    height: "55px",
                    border: "1px solid #dee2e6",
                    borderRadius: "12px",
                  }}
                >
                  <i className="fa fa-file-text-o fs-4" />
                </div>

                <div>
                  <h5 className="mb-1">{file.name}</h5>

                  <small className="text-muted">
                    {file.size < 1024 ? `${file.size} B` : `${(file.size / 1024).toFixed(1)} KB`}
                  </small>
                </div>
              </div>

              <button type="button" className="btn-close" onClick={removeFile} />
            </div>
          )}

          {/* Upload Progress */}
          {loading && (
            <div className="mt-3">
              <div className="progress" style={{ height: "8px" }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <small className="text-muted">{progress}% uploaded</small>
            </div>
          )}

          {/* Summary */}
          {summary && (
            <div className="mt-4">
              <div className="row g-3">
                {/* Total */}
                <div className="col-md-3">
                  <div className="card h-100 shadow-sm">
                    <div className="card-body d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: "10px",
                          height: "70px",
                          background: "#14213d",
                          borderRadius: "10px",
                        }}
                      />

                      <div>
                        <p className="text-muted mb-1 fw-semibold">total</p>
                        <h2 className="mb-0">{summary.total ?? 0}</h2>
                      </div>
                    </div>
                  </div>
                </div>

                {/* New */}
                <div className="col-md-3">
                  <div className="card h-100 shadow-sm">
                    <div className="card-body d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: "10px",
                          height: "70px",
                          background: "#4ca6a8",
                          borderRadius: "10px",
                        }}
                      />

                      <div>
                        <p className="text-muted mb-1 fw-semibold">new</p>
                        <h2 className="mb-0">{summary.inserted ?? 0}</h2>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Updated */}
                <div className="col-md-3">
                  <div className="card h-100 shadow-sm">
                    <div className="card-body d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: "10px",
                          height: "70px",
                          background: "#315c9b",
                          borderRadius: "10px",
                        }}
                      />

                      <div>
                        <p className="text-muted mb-1 fw-semibold">updated</p>
                        <h2 className="mb-0">0</h2>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Failed */}
                <div className="col-md-3">
                  <div className="card h-100 shadow-sm">
                    <div className="card-body d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: "10px",
                          height: "70px",
                          background: "#dc3545",
                          borderRadius: "10px",
                        }}
                      />

                      <div>
                        <p className="text-muted mb-1 fw-semibold">failed</p>
                        <h2 className="mb-0">{summary.failed ?? 0}</h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row Errors */}
              {summary.errors?.length > 0 && (
                <div className="alert alert-danger mt-3">
                  <ul className="mb-0">
                    {summary.errors.map((rowError, index) => (
                      <li key={index}>
                        {typeof rowError === "string"
                          ? rowError
                          : rowError?.message || JSON.stringify(rowError)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {summary?.errorFileUrl && (
                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = summary.errorFileUrl;
                      link.download = "product-import-errors.csv";
                      link.target = "_blank";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    <i className="fa fa-download me-2" />
                    Download Error File
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Import Button */}
          {file && !success && (
            <div className="text-end mt-4">
              <button className="btn btn-primary px-5" disabled={loading} onClick={handleUpload}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Importing...
                  </>
                ) : (
                  <>
                    <i className="fa fa-upload me-2" />
                    Import Products
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
